import type { UserConfig } from '@tunasong/manifest'
import manifest, { defaultHotkeys } from '@tunasong/manifest'
import { logger } from '@tunasong/models'
import type { CommandHotkey, Profile } from '@tunasong/schemas'
import invariant from 'tiny-invariant'
import { profilesApi } from '../api/profiles.js'
import type { UserConfigService } from '../configuration/config-service.js'
import { useStore } from '../configure-store.js'
import { features } from '../features/index.js'
import type { TunaThunkDispatch } from '../hooks/thunk-dispatch.hook.js'
import { debounceResolveAll } from '../util.js'

/**
 * Append any default hotkeys whose `commandId` is not already present in the
 * user's persisted array. Existing rows (including user customizations) are
 * preserved unchanged.
 *
 * Why: Zod `.default(...)` only fires on a missing field. Once a profile has
 * a non-empty `hotkeys` array, adding a new default in the schema does not
 * reach existing users — their array is replayed as-is on load. Forward-merge
 * here so new defaults appear for everyone, then persist so it's one-time per
 * user.
 *
 * The same drift pattern applies to any user-config array with `.default(...)`
 * (externalTools, plugins, etc.). Reuse this helper or follow its shape when
 * a new default is added to one of those.
 */
export const mergeMissingHotkeys = (
  user: readonly CommandHotkey[],
  defaults: readonly CommandHotkey[]
): { merged: CommandHotkey[]; added: CommandHotkey[] } => {
  const userCommandIds = new Set(user.map(h => h.commandId))
  const added = defaults.filter(d => !userCommandIds.has(d.commandId))
  const merged = added.length === 0 ? [...user] : [...user, ...added]
  return { merged, added }
}

type PluginConfig<T extends keyof UserConfig['plugins']> = UserConfig['plugins'][T]

/**
 * Async user configuration service. This loads configuration async, so clients that must
 * have updated information should use this and wait for the promise to resolve.
 *
 * Others can use the synchronous version(s) that look like useState() hooks.
 */

export class ConfigService implements UserConfigService {
  public ready = false

  private getState: ReturnType<typeof useStore>['getState'] | null = null
  private dispatch: TunaThunkDispatch | null = null
  private subscribe: ReturnType<typeof useStore>['subscribe'] | null = null

  private readyResolveFn: (() => void | undefined) | null = null
  private readyPromise: Promise<void>
  private profile: Profile | null = null
  private currentUserConfig: UserConfig | undefined

  private subscribers = new Set<(config: UserConfig) => void>()
  private debounceDelay: number

  private debouncedProfileUpdate

  constructor(debouncedDelay = 1000) {
    this.debounceDelay = debouncedDelay
    this.debouncedProfileUpdate = debounceResolveAll((profileId: string, config: UserConfig) => {
      invariant(this.dispatch, 'Cannot update profile without dispatch')
      return this.dispatch(
        profilesApi.endpoints.updateProfile.initiate({ id: profileId, profile: { config } })
      ).unwrap()
    }, this.debounceDelay)
    this.readyPromise = new Promise<void>(resolve => {
      this.readyResolveFn = resolve
    })
  }

  init = ({
    getState,
    dispatch,
    subscribe,
    profile,
  }: {
    getState: ReturnType<typeof useStore>['getState']
    dispatch: TunaThunkDispatch
    subscribe: ReturnType<typeof useStore>['subscribe']
    profile: Profile
  }) => {
    if (this.ready) {
      return
    }
    this.getState = getState
    this.dispatch = dispatch
    this.subscribe = subscribe
    this.profile = profile

    this.subscribe(() => {
      const userConfig = this.getState?.().config.userConfig
      if (userConfig !== this.currentUserConfig && userConfig) {
        this.currentUserConfig = userConfig

        for (const subscriber of this.subscribers) {
          subscriber(userConfig)
        }
      }
    })

    const config = manifest.userConfig.safeParse(profile.config)
    if (config.success) {
      // Forward-merge any default hotkeys that the persisted profile is missing.
      // See `mergeMissingHotkeys` for the rationale.
      const { merged, added } = mergeMissingHotkeys(config.data.hotkeys, defaultHotkeys)
      const migrated = added.length > 0 ? { ...config.data, hotkeys: merged } : config.data

      this.dispatch(features.config.actions.updateUserConfig(migrated))

      if (added.length > 0) {
        logger.info('Adding missing default hotkeys to user profile', {
          added: added.map(h => h.commandId),
        })
        // Persist back so this is one-time per user, not every load.
        this.debouncedProfileUpdate(profile.id, migrated).catch(err => {
          logger.error('Failed to persist migrated hotkeys', { err })
        })
      }
    } else {
      logger.error('Invalid user config', { config: profile.config })
      // if we have an invalid configuration, we'll just drop it
      // and update the profile with the default configuration at the next change.
    }

    invariant(this.readyResolveFn, 'readyResolveFn is not set')

    this.readyResolveFn()

    this.dispatch(features.config.actions.setUserConfigLoaded(true))
  }

  subscribeToChanges = (callback: (value: UserConfig) => void) => {
    if (!this.subscribers.has(callback)) {
      this.subscribers.add(callback)
    }
    return () => this.unsubscribeFromChanges(callback)
  }
  unsubscribeFromChanges = (callback: (value: UserConfig) => void) => {
    this.subscribers.delete(callback)
  }

  getUserConfig = async <T extends keyof UserConfig>(key: T) => {
    await this.readyPromise

    invariant(this.profile, `Cannot update config without a profile`)
    invariant(this.getState, `Cannot update config for without a state`)
    invariant(this.dispatch, `Cannot update config for without dispatch`)

    const userConfig = this.getState().config.userConfig
    invariant(userConfig, 'User config are not set')
    return userConfig[key]
  }

  getPluginConfig = async <T extends keyof UserConfig['plugins']>(pluginName: T) => {
    const config = await this.getUserConfig('plugins')
    return config[pluginName] ?? {}
  }

  updateUserConfigByKey = <T extends keyof UserConfig>(key: T, value: UserConfig[T]) => {
    invariant(this.profile, `Cannot set config for ${key} without a profile`)
    invariant(this.getState, `Cannot set config for ${key} without a state`)
    invariant(this.dispatch, `Cannot set config for ${key} without dispatch`)

    // Parse with Zod to ensure it's valid
    const newValue = manifest.userConfig.parse({
      ...this.getState().config.userConfig,
      [key]: value,
    })

    this.dispatch(features.config.actions.updateUserConfig(newValue))

    return this.debouncedProfileUpdate(this.profile.id, newValue)
  }

  updateUserConfigPartial = async (value: Partial<UserConfig>) => {
    invariant(this.profile, `Cannot update config without a profile`)
    invariant(this.getState, `Cannot update config for without a state`)
    invariant(this.dispatch, `Cannot update config for without dispatch`)

    // Parse with Zod to ensure it's valid
    const newValue = manifest.userConfig.parse({ ...this.getState().config.userConfig, ...value })

    this.dispatch(features.config.actions.updateUserConfig(newValue))

    return this.debouncedProfileUpdate(this.profile.id, newValue)
  }

  updateUserConfig = <T extends keyof UserConfig | Partial<UserConfig>>(
    keyOrValue: T,
    value?: T extends keyof UserConfig ? UserConfig[T] : never
  ) => {
    if (typeof keyOrValue === 'string') {
      return this.updateUserConfigByKey(keyOrValue, value)
    }
    return this.updateUserConfigPartial(keyOrValue)
  }

  updatePluginConfig = <T extends keyof UserConfig['plugins']>(pluginName: T, val: PluginConfig<T>) => {
    invariant(this.profile, `Cannot update config without a profile`)
    invariant(this.getState, `Cannot update config for without a state`)
    invariant(this.dispatch, `Cannot update config for without dispatch`)

    // First get the current value of "plugins", then update the plugin's value
    return this.updateUserConfigByKey('plugins', { ...this.getState().config.userConfig.plugins, [pluginName]: val })
  }
}
